import React, { Fragment, useState, useRef } from "react";
import NoUserImg from "../../../assets/images/no-results-bg.2d2c6ee3.png";
import { bottom, top, left, right } from "@popperjs/core";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import { classNames } from "../../../helpers/classNames";
import { usePopper } from "react-popper";
import Image from "../../elements/Image";
import Button from "./Button";

export default function SearchWithDropdown({
  label,
  labelClasses,
  placeholder,
  divClasses,
  inputClasses,
  iconClasses,
  iconPosition = "left",
  icon = "fa-regular fa-magnifying-glass",
  xPlacement,
  dropdownClass,
  dropdownData = [],
  keyword,
  loading,
  onChange = () => {},
  setKeyword = () => {},
}) {
  const [selected, setSelected] = useState("");
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [inputValue, setInputValue] = useState(keyword || "");

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement],
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setKeyword(value);
  };

  const handleSelection = (val) => {
    setSelected(val);
    setInputValue(val?.name || "");
    onChange(val);
  };

  const clearSelection = () => {
    setSelected(null);
    setInputValue("");
    setKeyword("");
    onChange(null);
  };

  const filteredData =
    inputValue === ""
      ? dropdownData
      : dropdownData.filter((item) => {
          const itemTitle = (item.name || "")
            ?.toLowerCase()
            .replace(/\s+/g, "");
          return itemTitle.includes(
            inputValue?.toLowerCase().replace(/\s+/g, ""),
          );
        });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const newName = e.target.value;
      onChange({ name: newName });
      setInputValue(newName || "");

      const history = JSON.parse(localStorage.getItem("history")) || [];
      const isDuplicate = history.some((item) => item.name === newName);
      if (isDuplicate) return;

      history.push({
        type: "list",
        icon: "fa-regular fa-clock-rotate-left",
        name: newName,
      });
      if (history.length > 5) history.shift();
      localStorage.setItem("history", JSON.stringify(history));
    }
  };

  return (
    <div className="relative w-full lg:w-auto">
      {label && (
        <div
          className={classNames(
            "text-sm font-medium text-slate-950 mb-1",
            labelClasses,
          )}
        >
          {label}
        </div>
      )}
      <Combobox
        value={selected}
        onChange={handleSelection}
        as={"div"}
        className="relative"
      >
        <div
          ref={setTargetElement}
          className={classNames(
            "relative flex h-9 items-center justify-center w-full lg:w-full bg-transparent rounded-full border border-shades-03 overflow-hidden",
            divClasses,
          )}
        >
          {iconPosition === "left" && (
            <ComboboxButton
              aria-label="Search"
              className={classNames(
                "h-8 aspect-square flex-shrink-0 flex items-center justify-center text-black text-base pr-2",
                iconClasses,
              )}
            >
              {loading ? (
                <i className="fa-duotone fa-spinner-third animate-spin" />
              ) : (
                <i className={classNames("fa-fw", icon)}></i>
              )}
            </ComboboxButton>
          )}

          <ComboboxInput
            displayValue={(item) => item?.title}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className={classNames(
              "searchInput !border-0 w-full !ring-0 bg-transparent text-black/80 text-lg py-0 px-0 placeholder:text-slate-500 dark:placeholder:text-slate-100 dark:text-gray-200  placeholder:text-base text-base",
              iconPosition === "left" && "pr-4",
              iconPosition === "right" && "pl-4",
              inputClasses,
            )}
            placeholder={placeholder}
          />

          {inputValue?.length > 0 && (
            <div className="h-12 aspect-square flex-shrink-0 flex items-center justify-center">
              <Button
                buttonFunction={clearSelection}
                buttonIcon={"fa-regular fa-times"}
                buttonIconPosition="left"
                buttonClasses="text-black w-6 h-6 bg-white/10 hover:bg-white/20 rounded-full text-2xl transition-all duration-300 before:bg-transparent after:bg-transparent text-sm text-black/70 hover:text-black px-0 cursor-pointer "
              />
            </div>
          )}

          {iconPosition === "right" && (
            <ComboboxButton
              className={classNames(
                "h-12 aspect-square flex-shrink-0 flex items-center justify-center text-black",
                iconClasses,
              )}
            >
              <i className={classNames("fa-fw", icon)}></i>
            </ComboboxButton>
          )}
        </div>

        <Transition
          as={Fragment}
          leave="transition duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="z-50 w-full"
          beforeEnter={() => setPopperElement(popperElRef.current)}
          afterLeave={() => setPopperElement(null)}
          ref={popperElRef}
          style={styles.popper}
          {...attributes.popper}
        >
          <ComboboxOptions
            className={classNames(
              "w-full min-w-[360px] origin-top-right bg-shades-05 border border-shades-03 divide-y divide-shades-03 rounded-2xl shadow ring-0 overflow-hidden",
              dropdownClass,
            )}
          >
            <div className="py-1 max-h-64 divide-y divide-shades-03 overflow-auto scroll-smooth scrollbar">
              {loading && (
                <div className="flex items-center space-x-4 text-black py-2 px-6">
                  <div className="h-20 w-20 flex items-center justify-center">
                    <span className="select_loader"></span>
                  </div>
                  <div className="text-shades-0">Searching for results...</div>
                </div>
              )}
              {filteredData?.length > 0 &&
                filteredData.map((item, index) => (
                  <ComboboxOption
                    key={index}
                    className={({ active }) =>
                      classNames(
                        "relative select-none py-3 pr-4 pl-4 text-base leading-tight cursor-pointer transition-all duration-200 text-black flex items-center space-x-2 ",
                        active ? "bg-shades-07 text-black" : "",
                      )
                    }
                    value={item}
                  >
                    {({ selected }) => (
                      <>
                        {item?.type === "user" && (
                          <div className="flex items-center gap-2 pl-2">
                            <div className="flex items-center justify-center w-8 lg:w-12 aspect-square rounded-full overflow-hidden border">
                              <Image
                                src={item.image || NoUserImg}
                                width={100}
                                height={100}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <div className="text-xs lg:text-sm">
                                {item.name}
                              </div>
                              {item.subItem && (
                                <div className="text-xs text-text-disabled">
                                  {item.subItem}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {item?.type === "list" && (
                          <div className="flex flex-row w-full">
                            <span className="text-black text-xs lg:text-sm">
                              {item.name}
                            </span>
                            {item?.icon && (
                              <span className="text-black text-xs lg:text-sm ml-auto">
                                <i className={item?.icon}></i>
                              </span>
                            )}
                          </div>
                        )}
                        {item?.type !== "list" &&
                          item?._id === selected?._id && (
                            <div className="absolute inset-y-0 left-0 flex items-center text-slate-800 text-base">
                              <i className="fa-regular fa-fw fa-check"></i>
                            </div>
                          )}
                      </>
                    )}
                  </ComboboxOption>
                ))}
              {/* {filteredData?.length === 0 && !loading && (
                <div className="flex flex-col sm:flex-row items-center space-y-4 md:space-x-8 text-black py-4 md:py-8 px-4 md:px-8">
                  <i className="fa-light fa-face-sad-tear text-3xl md:text-5xl"></i>
                  <div className="flex flex-col space-y-1 text-center md:text-left">
                    <div className="font-semibold">No Results Found!</div>
                    <div className="text-sm text-black/75">
                      Try refining your search with a different name.
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          </ComboboxOptions>
        </Transition>
      </Combobox>
    </div>
  );
}
